<app-admin-modal
  title="Import Leads"
  size="xl"
  variant="lead-import"
  (closed)="onModalClose()"
>
  <div class="lead-import-modal">
    <nav class="lead-import-steps" aria-label="Import progress">
      @for (item of wizardStepStates(); track item.id; let last = $last) {
        <div
          class="lead-import-steps__item"
          [class.is-done]="item.status === 'done'"
          [class.is-active]="item.status === 'active'"
        >
          <span class="lead-import-steps__marker" aria-hidden="true">
            @if (item.status === 'done') {
              <app-admin-icon name="check" [size]="14" />
            } @else {
              {{ item.number }}
            }
          </span>
          <span class="lead-import-steps__label">{{ item.label }}</span>
        </div>
        @if (!last) {
          <span class="lead-import-steps__line" [class.is-done]="item.status === 'done'" aria-hidden="true"></span>
        }
      }
    </nav>

    <div class="lead-import-modal__body">
    <header class="lead-import-modal__header">
      <h3 class="lead-import-modal__title">{{ stepHeading().title }}</h3>
      <p class="lead-import-modal__description">{{ stepHeading().description }}</p>
    </header>

    @if (error()) {
      <div class="lead-import-alert lead-import-alert--error" role="alert">
        <app-admin-icon name="circle-alert" [size]="18" />
        <span>{{ error() }}</span>
      </div>
    }

    @if (selectedFileName() && step() !== 'upload') {
      <div class="lead-import-file-chip">
        <app-admin-icon name="file-spreadsheet" [size]="16" />
        <span>{{ selectedFileName() }}</span>
        <span class="lead-import-file-chip__meta">{{ parsedRowCount() }} rows</span>
      </div>
    }

    @switch (step()) {
      @case ('upload') {
        <div class="lead-import-panel lead-import-panel--upload">
          <label
            class="lead-import-dropzone"
            [class.is-dragging]="dragActive()"
            [class.is-loading]="loading()"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
          >
            <input type="file" accept=".xlsx,.xls,.csv" [disabled]="loading()" (change)="onFileSelected($event)" />
            <span class="lead-import-dropzone__icon" aria-hidden="true">
              @if (loading()) {
                <app-admin-icon name="loader-circle" [size]="36" />
              } @else {
                <app-admin-icon name="upload" [size]="36" />
              }
            </span>
            <span class="lead-import-dropzone__title">
              {{ loading() ? 'Reading your file…' : 'Drop your file here or click to browse' }}
            </span>
            <span class="lead-import-dropzone__hint">Excel (.xlsx, .xls) or CSV · up to 5,000 rows</span>
          </label>

          <div class="lead-import-info-grid">
            <div class="lead-import-info-card lead-import-info-card--required">
              <app-admin-icon name="asterisk" [size]="18" />
              <div>
                <strong>Required columns</strong>
                <p>Full name and phone number must be present in every row you want to import.</p>
              </div>
            </div>
            <div class="lead-import-info-card lead-import-info-card--optional">
              <app-admin-icon name="info" [size]="18" />
              <div>
                <strong>Optional columns</strong>
                <p>Email and preferred destination can be empty — those rows import with a warning, not an error.</p>
              </div>
            </div>
          </div>
        </div>
      }

      @case ('mapping') {
        <div class="lead-import-panel">
          <div class="lead-import-panel__toolbar">
            <p class="lead-import-panel__hint">
              <app-admin-icon name="sparkles" [size]="16" />
              Columns were auto-mapped where possible. Review anything marked “Not mapped”.
            </p>
            <app-admin-button variant="secondary" type="button" (click)="rerunAutoMap()">Re-run auto-map</app-admin-button>
          </div>
          <div class="lead-import-table-wrap">
            <app-admin-table>
              <thead>
                <tr>
                  <th>Your column</th>
                  <th>Maps to</th>
                  <th>Sample data</th>
                </tr>
              </thead>
              <tbody>
                @for (header of parsedFile()?.headers ?? []; track header) {
                  <tr [class]="'lead-import-map-row--' + mappingStatus(header)">
                    <td>
                      <strong>{{ header }}</strong>
                      @if (mappingStatus(header) === 'unmapped') {
                        <span class="lead-import-map-tag lead-import-map-tag--warn">Needs mapping</span>
                      }
                    </td>
                    <td>
                      <select
                        class="cg-select"
                        [ngModel]="columnMapping()[header]"
                        (ngModelChange)="onMappingChange(header, $event)"
                        [name]="'map-' + header"
                      >
                        @for (option of mappingOptions(); track option.value) {
                          <option [value]="option.value">{{ option.label }}</option>
                        }
                      </select>
                    </td>
                    <td class="lead-import-sample">{{ sampleValues(header) || '—' }}</td>
                  </tr>
                }
              </tbody>
            </app-admin-table>
          </div>
          <footer class="lead-import-footer">
            <app-admin-button variant="secondary" type="button" (click)="step.set('upload')">Back</app-admin-button>
            <app-admin-button variant="primary" type="button" (click)="goToStreamStep()">Continue to settings</app-admin-button>
          </footer>
        </div>
      }

      @case ('stream') {
        <div class="lead-import-panel">
          <div class="lead-import-table-wrap">
            <app-admin-table>
              <thead>
                <tr>
                  <th>Value in your file</th>
                  <th>Map to stream</th>
                </tr>
              </thead>
              <tbody>
                @for (value of unknownStreamValues(); track value) {
                  <tr>
                    <td><code class="lead-import-code">{{ value }}</code></td>
                    <td>
                      <select
                        class="cg-select"
                        [ngModel]="streamMappingFor(value)"
                        (ngModelChange)="setStreamMapping(value, $event)"
                        [name]="'stream-' + value"
                      >
                        <option value="">Leave blank</option>
                        @for (stream of streamOptions; track stream) {
                          <option [value]="stream">{{ stream }}</option>
                        }
                      </select>
                    </td>
                  </tr>
                }
              </tbody>
            </app-admin-table>
          </div>
          <footer class="lead-import-footer">
            <app-admin-button variant="secondary" type="button" (click)="step.set('mapping')">Back</app-admin-button>
            <app-admin-button variant="primary" type="button" (click)="goToDuplicateStep()">Continue</app-admin-button>
          </footer>
        </div>
      }

      @case ('duplicate') {
        <div class="lead-import-panel">
          <div class="lead-import-option-cards">
            <button
              type="button"
              class="lead-import-option-card"
              [class.is-selected]="duplicateAction() === 'skip'"
              (click)="setDuplicateAction('skip')"
            >
              <strong>Skip duplicates</strong>
              <span>Do not import rows that match an existing lead. Safest option.</span>
            </button>
            <button
              type="button"
              class="lead-import-option-card"
              [class.is-selected]="duplicateAction() === 'update'"
              (click)="setDuplicateAction('update')"
            >
              <strong>Update existing leads</strong>
              <span>Fill in missing fields on matching leads using data from your file.</span>
            </button>
            <button
              type="button"
              class="lead-import-option-card"
              [class.is-selected]="duplicateAction() === 'create'"
              (click)="setDuplicateAction('create')"
            >
              <strong>Create anyway</strong>
              <span>Import every row even if a lead with the same phone or email already exists.</span>
            </button>
          </div>
          <footer class="lead-import-footer">
            <app-admin-button variant="secondary" type="button" (click)="step.set(unknownStreamValues().length ? 'stream' : 'mapping')">Back</app-admin-button>
            <app-admin-button variant="primary" type="button" [disabled]="!duplicateAction() || loading()" (click)="runPreview()">
              {{ loading() ? 'Building preview…' : 'Preview import' }}
            </app-admin-button>
          </footer>
        </div>
      }

      @case ('preview') {
        <div class="lead-import-panel">
          @if (preview(); as result) {
            <div class="lead-import-stats">
              <button type="button" class="lead-import-stat" [class.is-active]="previewFilter() === 'all'" (click)="setPreviewFilter('all')">
                <span class="lead-import-stat__value">{{ result.aggregates.total }}</span>
                <span class="lead-import-stat__label">Total rows</span>
              </button>
              <button type="button" class="lead-import-stat lead-import-stat--valid" [class.is-active]="previewFilter() === 'valid'" (click)="setPreviewFilter('valid')">
                <span class="lead-import-stat__value">{{ result.aggregates.valid }}</span>
                <span class="lead-import-stat__label">Ready</span>
              </button>
              <button type="button" class="lead-import-stat lead-import-stat--warning" [class.is-active]="previewFilter() === 'warning'" (click)="setPreviewFilter('warning')">
                <span class="lead-import-stat__value">{{ result.aggregates.warning }}</span>
                <span class="lead-import-stat__label">Warnings</span>
              </button>
              <button type="button" class="lead-import-stat lead-import-stat--duplicate" [class.is-active]="previewFilter() === 'duplicate'" (click)="setPreviewFilter('duplicate')">
                <span class="lead-import-stat__value">{{ result.aggregates.duplicate }}</span>
                <span class="lead-import-stat__label">Duplicates</span>
              </button>
              <button type="button" class="lead-import-stat lead-import-stat--error" [class.is-active]="previewFilter() === 'error'" (click)="setPreviewFilter('error')">
                <span class="lead-import-stat__value">{{ result.aggregates.error }}</span>
                <span class="lead-import-stat__label">Errors</span>
              </button>
            </div>

            <div class="lead-import-table-wrap">
              <app-admin-table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  @if (paginatedPreviewRows().length === 0) {
                    <tr>
                      <td colspan="6"><p class="lead-import-empty">No rows match this filter.</p></td>
                    </tr>
                  } @else {
                    @for (row of paginatedPreviewRows(); track row.rowIndex) {
                      <tr [class]="'lead-import-row--' + row.status">
                        <td>{{ row.rowIndex }}</td>
                        <td>
                          <span class="lead-import-badge lead-import-badge--{{ row.status }}">{{ statusLabel(row.status) }}</span>
                        </td>
                        <td>{{ row.fields['fullName'] || '—' }}</td>
                        <td>{{ row.fields['phoneNumber'] || '—' }}</td>
                        <td>{{ row.fields['email'] || '—' }}</td>
                        <td class="lead-import-details">
                          @if (row.errors.length) {
                            <div>{{ row.errors.join(' ') }}</div>
                          }
                          @if (row.warnings.length) {
                            <div>{{ row.warnings.join(' ') }}</div>
                          }
                          @if (row.existingLead) {
                            <div>Existing: {{ row.existingLead.fullName }}</div>
                          }
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </app-admin-table>
            </div>

            @if (filteredPreviewRows().length > previewPageSize) {
              <div class="lead-import-pagination">
                <span class="lead-import-pagination__summary">{{ previewPageSummary() }}</span>
                <div class="lead-import-pagination__actions">
                  <app-admin-button variant="secondary" type="button" [disabled]="previewPage() <= 1" (click)="goToPreviewPage(previewPage() - 1)">Previous</app-admin-button>
                  <app-admin-button variant="secondary" type="button" [disabled]="previewPage() >= previewPageCount()" (click)="goToPreviewPage(previewPage() + 1)">Next</app-admin-button>
                </div>
              </div>
            }

            <div class="lead-import-confirm">
              @if (preview()!.aggregates.warning > 0) {
                <label class="lead-import-checkbox">
                  <input type="checkbox" [checked]="includeWarnings()" (change)="setIncludeWarnings($any($event.target).checked)" name="includeWarnings" />
                  Also import {{ preview()!.aggregates.warning }} row(s) with warnings
                </label>
              }
              <p class="lead-import-confirm__note">Rows with errors are always skipped.</p>
            </div>
          }

          <footer class="lead-import-footer">
            <app-admin-button variant="secondary" type="button" (click)="step.set('duplicate')">Back</app-admin-button>
            <app-admin-button variant="primary" type="button" [disabled]="!canImport()" (click)="executeImport()">
              {{ executing() ? 'Importing…' : 'Import ' + importableCount() + ' lead(s)' }}
            </app-admin-button>
          </footer>
        </div>
      }

      @case ('importing') {
        <div class="lead-import-panel lead-import-panel--center">
          <div class="lead-import-progress">
            <div class="lead-import-progress__ring" aria-hidden="true">
              <app-admin-icon name="loader-circle" [size]="48" />
            </div>
            <div class="lead-import-progress__labels">
              <span>
                <strong>{{ importProgress().processed }}</strong> of <strong>{{ importProgress().total }}</strong> rows processed
              </span>
              <span>{{ importProgressPercent() }}%</span>
            </div>
            <div
              class="lead-import-progress__track"
              role="progressbar"
              [attr.aria-valuenow]="importProgress().processed"
              [attr.aria-valuemin]="0"
              [attr.aria-valuemax]="importProgress().total"
              [attr.aria-label]="'Import progress: ' + importProgress().processed + ' of ' + importProgress().total + ' rows'"
            >
              <div class="lead-import-progress__bar" [style.width.%]="importProgressPercent()"></div>
            </div>
            <p class="lead-import-progress__remaining">{{ importRemaining() }} row(s) remaining</p>
          </div>
        </div>
      }

      @case ('results') {
        <div class="lead-import-panel">
          @if (executeResult(); as result) {
            <div class="lead-import-results">
              <div class="lead-import-result-card lead-import-result-card--success">
                <span class="lead-import-result-card__value">{{ result.created }}</span>
                <span class="lead-import-result-card__label">Created</span>
              </div>
              <div class="lead-import-result-card lead-import-result-card--info">
                <span class="lead-import-result-card__value">{{ result.updated }}</span>
                <span class="lead-import-result-card__label">Updated</span>
              </div>
              <div class="lead-import-result-card">
                <span class="lead-import-result-card__value">{{ result.skippedDuplicates }}</span>
                <span class="lead-import-result-card__label">Skipped duplicates</span>
              </div>
              <div class="lead-import-result-card">
                <span class="lead-import-result-card__value">{{ result.skippedErrors + result.skippedWarnings }}</span>
                <span class="lead-import-result-card__label">Skipped rows</span>
              </div>
              @if (result.failed > 0) {
                <div class="lead-import-result-card lead-import-result-card--error">
                  <span class="lead-import-result-card__value">{{ result.failed }}</span>
                  <span class="lead-import-result-card__label">Failed</span>
                </div>
              }
            </div>

            @if (result.errors.length > 0) {
              <div class="lead-import-alert lead-import-alert--warn">
                <app-admin-icon name="triangle-alert" [size]="18" />
                <span>{{ result.errors.length }} row(s) had errors. Download the report to review them.</span>
              </div>
              <div class="lead-import-footer lead-import-footer--inline">
                <app-admin-button variant="secondary" type="button" (click)="downloadErrorReport('xlsx')">Download Excel report</app-admin-button>
                <app-admin-button variant="secondary" type="button" (click)="downloadErrorReport('csv')">Download CSV report</app-admin-button>
              </div>
            }
          }

          <footer class="lead-import-footer">
            <app-admin-button variant="primary" type="button" (click)="finishImport()">Done — back to leads</app-admin-button>
            <app-admin-button variant="secondary" type="button" (click)="startNewImport()">Import another file</app-admin-button>
          </footer>
        </div>
      }
    }
    </div>
  </div>
</app-admin-modal>
